// «Письмо выбранным» of a table (stage J6): a template → who gets it (the selected rows, or the whole selection the table shows) →
// the preview with the count → send. The server makes a record «Рассылка» with the totals; each letter keeps its own status.

import { useEffect, useMemo, useState } from 'react';
import { markdownToHtml, recordTitle, type Id, type RecordObj } from '@confdeck/core-shared';
import { navigate } from '../shell/router.ts';
import { useMeta, useSync, useTypesTick } from '../sync/react.tsx';
import { Button, Dialog, useToast, Select } from '../ui/primitives.tsx';
import r from '../record/record.module.css';
import s from './mail.module.css';

type Preview = { count: number; skipped: number; sample: { to: string; subject: string; body: string } | null };

async function bulk<T>(input: Record<string, unknown>): Promise<{ ok: true; result: T } | { ok: false; message: string }> {
  const res = await fetch('/api/commands/mail.bulk', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
  const body = await res.json().catch(() => ({})) as { result?: T; error?: { message: string } };
  return res.ok ? { ok: true, result: body.result as T } : { ok: false, message: body.error?.message ?? 'Не получилось' };
}

export function BulkMail({ typeId, ids, label, onClose }: { typeId: Id; ids: Id[]; label: string; onClose: () => void }) {
  const meta = useMeta();
  const client = useSync();
  const toast = useToast();
  const tplType = meta.typeByKey('email_template');
  useTypesTick(tplType ? [tplType.id] : []);
  const templates = useMemo(() => (tplType ? [...client.store.recordIds(tplType.id)].map((x) => client.store.record(x)).filter((x): x is RecordObj => !!x && !x.archivedAt && !x.data.disabled) : []), [tplType, client.store, client.store.typeVersion.get(tplType?.id ?? '')]);
  const [template, setTemplate] = useState<Id | ''>('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The preview follows the template: how many letters go, how many rows have no address, and the first letter as it will read.
  useEffect(() => {
    setPreview(null); setError(null);
    if (!template) return;
    let live = true;
    void bulk<Preview>({ typeId, template, ids, dryRun: true }).then((x) => { if (!live) return; if (x.ok) setPreview(x.result); else setError(x.message); });
    return () => { live = false; };
  }, [template, typeId, ids]);

  const send = async () => {
    setBusy(true);
    const x = await bulk<{ campaignId: Id; count: number }>({ typeId, template, ids, dryRun: false });
    setBusy(false);
    if (!x.ok) { setError(x.message); return; }
    toast({ kind: 'info', text: `Рассылка отправлена: писем — ${x.result.count}` });
    onClose();
    navigate(`/r/${x.result.campaignId}`);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} label="Письмо выбранным">
      <div className={r.dialog}>
        <h2 className={r.dialogTitle}>Письмо: {label}</h2>
        <label className={r.dialogField}>
          <span>Шаблон</span>
          <Select search label="Шаблон письма" placeholder="Выберите шаблон…" value={template} onChange={setTemplate} options={templates.map((t) => ({ value: t.id, label: recordTitle(t, meta) }))} />
        </label>
        {!templates.length && <p className={s.note}>Шаблонов нет: создайте его в «Письма → Шаблоны писем».</p>}
        {preview && (
          <div className={s.box} aria-label="Предпросмотр">
            <p className={s.title}>Получат {preview.count}{preview.skipped ? ` · без почты или повтор адреса — ${preview.skipped}` : ''}</p>
            {preview.sample && <>
              <div className={s.row}><span className={s.label}>Кому</span><span className={s.text}>{preview.sample.to}</span></div>
              <div className={s.row}><span className={s.label}>Тема</span><span className={`${s.text} ${s.subject}`}>{preview.sample.subject}</span></div>
              <div className={s.row}><span className={s.label}>Текст</span><div className={s.letter} dangerouslySetInnerHTML={{ __html: markdownToHtml(preview.sample.body, { record: () => null, file: () => null }) }} /></div>
            </>}
          </div>
        )}
        {error && <div className={r.dialogError} role="alert">{error}</div>}
        <div className={r.dialogFoot}>
          <Button onClick={onClose}>Отмена</Button>
          <Button variant="primary" disabled={busy || !preview?.count} onClick={() => void send()}>{preview?.count ? `Отправить ${preview.count}` : 'Отправить'}</Button>
        </div>
      </div>
    </Dialog>
  );
}
